"use client";

import React, { useEffect, useRef, useState } from "react";

function formatValue(value: string) {
  const regex = /(\$|%|\+|<|>|→|&lt;|&gt;)/g;
  const parts = value.split(regex);
  return parts.map((part, i) => {
    if (regex.test(part)) {
      return (
        <span key={i} className="text-brass-soft">
          {part}
        </span>
      );
    }
    return part;
  });
}

function parseNumericValue(value: string) {
  const regex = /^(.*?)(\d[\d,]*\.\d+|\d[\d,]*)(.*)$/;
  const match = value.match(regex);
  if (!match) return null;

  const prefix = match[1];
  const rawNumStr = match[2];
  const suffix = match[3];
  const cleanNumStr = rawNumStr.replace(/,/g, "");
  const targetNum = parseFloat(cleanNumStr);
  if (isNaN(targetNum)) return null;

  const decimals = cleanNumStr.includes(".") ? cleanNumStr.split(".")[1].length : 0;
  const hasCommas = rawNumStr.includes(",");

  return { prefix, targetNum, decimals, suffix, hasCommas };
}

function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}

interface AnimatedValueProps {
  value: string;
  duration?: number;
}

export function AnimatedValue({ value, duration = 1600 }: AnimatedValueProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [displayValue, setDisplayValue] = useState<string>(value);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const parsed = parseNumericValue(value);
    if (!parsed) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasAnimatedRef.current) {
          hasAnimatedRef.current = true;

          const { prefix, targetNum, decimals, suffix, hasCommas } = parsed;
          const startTime = performance.now();

          const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeOutCubic(progress);
            const currentNum = targetNum * easedProgress;

            let formattedNum = currentNum.toFixed(decimals);
            if (hasCommas) {
              const parts = formattedNum.split(".");
              parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              formattedNum = parts.join(".");
            }

            setDisplayValue(`${prefix}${formattedNum}${suffix}`);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setDisplayValue(value);
            }
          };

          requestAnimationFrame(animate);
          observer.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -50px 0px" }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [value, duration]);

  return <span ref={containerRef}>{formatValue(displayValue)}</span>;
}
